import React, { useState, useEffect } from 'react';
import { availabilityAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Clock,
  Save,
  Loader2,
  Calendar,
  Plus,
  X,
  Check,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const DAYS = [
  { key: 'monday', label: 'Lundi' },
  { key: 'tuesday', label: 'Mardi' },
  { key: 'wednesday', label: 'Mercredi' },
  { key: 'thursday', label: 'Jeudi' },
  { key: 'friday', label: 'Vendredi' },
  { key: 'saturday', label: 'Samedi' },
  { key: 'sunday', label: 'Dimanche' },
];

// Generate time slots from 06:00 to 22:00
const generateTimeSlots = () => {
  const slots = [];
  for (let hour = 6; hour <= 22; hour++) {
    slots.push(`${hour.toString().padStart(2, '0')}:00`);
    slots.push(`${hour.toString().padStart(2, '0')}:30`);
  }
  return slots;
};

const ALL_TIME_SLOTS = generateTimeSlots();

const AvailabilityManager = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [schedule, setSchedule] = useState({});
  const [settings, setSettings] = useState({
    slot_duration: 30,
    buffer_time: 0,
    max_appointments_per_day: 20,
    advance_booking_days: 30,
  });

  useEffect(() => {
    loadAvailability();
  }, []);

  const loadAvailability = async () => {
    try {
      const response = await availabilityAPI.get();
      const data = response.data.availability;
      setSchedule(data.schedule || getDefaultSchedule());
      setSettings({
        slot_duration: data.slot_duration || 30,
        buffer_time: data.buffer_time || 0,
        max_appointments_per_day: data.max_appointments_per_day || 20,
        advance_booking_days: data.advance_booking_days || 30,
      });
    } catch (error) {
      console.error('Error loading availability:', error);
      // Use default schedule on error
      setSchedule(getDefaultSchedule());
    } finally {
      setLoading(false);
    }
  };

  const getDefaultSchedule = () => {
    const defaultSlots = [];
    for (let hour = 8; hour < 18; hour++) {
      defaultSlots.push(`${hour.toString().padStart(2, '0')}:00`);
      defaultSlots.push(`${hour.toString().padStart(2, '0')}:30`);
    }
    
    return {
      monday: { enabled: true, slots: [...defaultSlots] },
      tuesday: { enabled: true, slots: [...defaultSlots] },
      wednesday: { enabled: true, slots: [...defaultSlots] },
      thursday: { enabled: true, slots: [...defaultSlots] },
      friday: { enabled: true, slots: [...defaultSlots] },
      saturday: { enabled: false, slots: [] },
      sunday: { enabled: false, slots: [] },
    };
  };

  const toggleDay = (dayKey) => {
    setSchedule(prev => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        enabled: !prev[dayKey].enabled,
      }
    }));
  };

  const toggleSlot = (dayKey, slot) => {
    setSchedule(prev => {
      const currentSlots = prev[dayKey]?.slots || [];
      const newSlots = currentSlots.includes(slot)
        ? currentSlots.filter(s => s !== slot)
        : [...currentSlots, slot].sort();
      
      return {
        ...prev,
        [dayKey]: {
          ...prev[dayKey],
          slots: newSlots,
        }
      };
    });
  };

  const selectAllSlots = (dayKey, startHour = 8, endHour = 18) => {
    const slots = [];
    for (let hour = startHour; hour < endHour; hour++) {
      slots.push(`${hour.toString().padStart(2, '0')}:00`);
      slots.push(`${hour.toString().padStart(2, '0')}:30`);
    }
    setSchedule(prev => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        enabled: true,
        slots: slots,
      }
    }));
  };

  const clearAllSlots = (dayKey) => {
    setSchedule(prev => ({
      ...prev,
      [dayKey]: {
        ...prev[dayKey],
        slots: [],
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await availabilityAPI.update({
        schedule,
        ...settings,
      });
      toast.success('Disponibilités enregistrées avec succès !');
    } catch (error) {
      console.error('Error saving availability:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const copyToAllDays = (sourceDayKey) => {
    const sourceDay = schedule[sourceDayKey];
    if (!sourceDay) return;
    
    setSchedule(prev => {
      const newSchedule = { ...prev };
      DAYS.forEach(day => {
        if (day.key !== sourceDayKey && day.key !== 'saturday' && day.key !== 'sunday') {
          newSchedule[day.key] = { ...sourceDay };
        }
      });
      return newSchedule;
    });
    toast.success(`Créneaux de ${DAYS.find(d => d.key === sourceDayKey)?.label} copiés sur les jours de semaine`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-[#E53935] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="availability-manager">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-[#1A237E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Mes disponibilités
          </h2>
          <p className="text-slate-500 text-sm mt-1">
            Définissez vos créneaux de consultation disponibles
          </p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#E53935] hover:bg-[#C62828] text-white"
          data-testid="save-availability-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Enregistrer
        </Button>
      </div>

      {/* Info Card */}
      <Card className="border-0 shadow-sm bg-gradient-to-r from-blue-50 to-indigo-50">
        <CardContent className="p-4 flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
            <AlertCircle className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-medium text-blue-900">Comment ça fonctionne ?</h3>
            <p className="text-sm text-blue-700 mt-1">
              Activez les jours où vous travaillez, puis sélectionnez les créneaux horaires disponibles.
              Les patients pourront uniquement réserver sur ces créneaux.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Schedule Grid */}
      <div className="space-y-4">
        {DAYS.map((day) => {
          const dayData = schedule[day.key] || { enabled: false, slots: [] };
          const isEnabled = dayData.enabled;
          const slots = dayData.slots || [];
          
          return (
            <Card 
              key={day.key} 
              className={`border-0 shadow-sm transition-all ${isEnabled ? 'bg-white' : 'bg-slate-50'}`}
              data-testid={`day-${day.key}`}
            >
              <CardContent className="p-3 sm:p-4">
                {/* Day Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={isEnabled}
                      onCheckedChange={() => toggleDay(day.key)}
                      data-testid={`toggle-${day.key}`}
                    />
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`font-semibold ${isEnabled ? 'text-slate-900' : 'text-slate-400'}`}>
                        {day.label}
                      </span>
                      {isEnabled && (
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                          {slots.length} créneaux
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  {isEnabled && (
                    <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => selectAllSlots(day.key, 8, 12)}
                        className="text-[10px] sm:text-xs h-7 px-2 sm:px-3"
                      >
                        Matin
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => selectAllSlots(day.key, 14, 18)}
                        className="text-[10px] sm:text-xs h-7 px-2 sm:px-3"
                      >
                        Ap-midi
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => selectAllSlots(day.key, 8, 18)}
                        className="text-[10px] sm:text-xs h-7 px-2 sm:px-3"
                      >
                        Journée
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => clearAllSlots(day.key)}
                        className="text-xs h-7 w-7 p-0 text-red-500 hover:text-red-700"
                      >
                        <X className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToAllDays(day.key)}
                        className="text-[10px] sm:text-xs h-7 px-2 text-blue-600 hover:text-blue-800"
                        title="Copier sur tous les jours de semaine"
                      >
                        Copier
                      </Button>
                    </div>
                  )}
                </div>
                
                {/* Time Slots Grid - Optimized for mobile */}
                {isEnabled && (
                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12 gap-1.5 sm:gap-2">
                    {ALL_TIME_SLOTS.map((slot) => {
                      const isSelected = slots.includes(slot);
                      return (
                        <button
                          key={slot}
                          type="button"
                          onClick={() => toggleSlot(day.key, slot)}
                          className={`py-2.5 px-0.5 text-[11px] sm:text-xs rounded-lg font-semibold transition-all min-w-0 ${
                            isSelected
                              ? 'bg-[#E53935] text-white shadow-sm'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                          }`}
                          data-testid={`slot-${day.key}-${slot}`}
                        >
                          {slot}
                        </button>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Advanced Settings */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#1976D2]" />
            Paramètres avancés
          </CardTitle>
          <CardDescription>
            Configurez la durée des consultations et les limites de réservation
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label className="text-sm">Durée d'un créneau</Label>
              <Select
                value={settings.slot_duration.toString()}
                onValueChange={(v) => setSettings(prev => ({ ...prev, slot_duration: parseInt(v) }))}
              >
                <SelectTrigger className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="45">45 minutes</SelectItem>
                  <SelectItem value="60">1 heure</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm">Temps tampon entre RDV</Label>
              <Select
                value={settings.buffer_time.toString()}
                onValueChange={(v) => setSettings(prev => ({ ...prev, buffer_time: parseInt(v) }))}
              >
                <SelectTrigger className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Aucun</SelectItem>
                  <SelectItem value="5">5 minutes</SelectItem>
                  <SelectItem value="10">10 minutes</SelectItem>
                  <SelectItem value="15">15 minutes</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm">Max RDV par jour</Label>
              <Input
                type="number"
                min="1"
                max="50"
                value={settings.max_appointments_per_day}
                onChange={(e) => setSettings(prev => ({ ...prev, max_appointments_per_day: parseInt(e.target.value) || 20 }))}
                className="h-11"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-sm">Réservation max (jours)</Label>
              <Select
                value={settings.advance_booking_days.toString()}
                onValueChange={(v) => setSettings(prev => ({ ...prev, advance_booking_days: parseInt(v) }))}
              >
                <SelectTrigger className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">1 semaine</SelectItem>
                  <SelectItem value="14">2 semaines</SelectItem>
                  <SelectItem value="30">1 mois</SelectItem>
                  <SelectItem value="60">2 mois</SelectItem>
                  <SelectItem value="90">3 mois</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Button (Mobile) */}
      <div className="lg:hidden fixed bottom-20 left-4 right-4">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-[#E53935] hover:bg-[#C62828] text-white h-12 rounded-xl shadow-lg"
        >
          {saving ? (
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
          ) : (
            <Save className="w-5 h-5 mr-2" />
          )}
          Enregistrer les disponibilités
        </Button>
      </div>
    </div>
  );
};

export default AvailabilityManager;
